import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import {
  type ChatReadPosition,
  buildChatAnchorPosition,
  buildChatLatestPosition,
  buildChatReadPositionStorageKey,
  getChatReadPositionForSave,
  getLastVisibleChatMessageId,
  isChatScrolledNearBottom,
  parseChatReadPosition,
  resolveChatReadPositionTarget,
  serializeChatReadPosition,
} from "@sportex100/shared/utils/chatReadPosition";
import { formatChatMessageTimestamp } from "@sportex100/shared/utils/chatTimestamp";
import { cn } from "../../lib/utils";
import { Button } from "./Button";
import { useChat, ChatMessage } from "../../hooks/useChat";
import { getChatWidgetPanelClassName } from "./chatWidgetLayout";
import { TeamCodeBadge } from "./TeamCodeBadge";
import {
  type ChatComposerDraft,
  type ChatOrderPromotionAttachment,
  getValidOrderPromotionAttachment,
} from "../../lib/chatPromotions";

interface ChatWidgetProps {
  poolCode: string | null;
  poolName: string | null;
  traderCode: string | null;
  displayName: string;
  isAuthenticated: boolean;
  composerDraft?: ChatComposerDraft | null;
  onComposerDraftConsumed?: () => void;
  onOpenPromotedOrder?: (attachment: ChatOrderPromotionAttachment) => void;
}

function ChatRestoreSkeleton() {
  const rows = [
    { key: "restore-1", width: "w-1/2", own: false },
    { key: "restore-2", width: "w-2/3", own: true },
    { key: "restore-3", width: "w-5/12", own: false },
    { key: "restore-4", width: "w-3/4", own: false },
    { key: "restore-5", width: "w-7/12", own: true },
  ];

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-10 space-y-4 bg-background p-4"
    >
      {rows.map((row) => (
        <div
          key={row.key}
          className={cn(
            "flex items-start gap-2",
            row.own ? "justify-end" : "justify-start"
          )}
        >
          {!row.own && (
            <div className="h-8 w-8 shrink-0 rounded-full border border-border-subtle bg-background-raised/80" />
          )}
          <div
            className={cn(
              "h-11 rounded-2xl border border-border-subtle bg-background-raised/80",
              row.own ? "rounded-br-md bg-brand/25" : "rounded-bl-md",
              row.width
            )}
          />
          {row.own && (
            <div className="h-8 w-8 shrink-0 rounded-full border border-border-subtle bg-background-raised/80" />
          )}
        </div>
      ))}
    </div>
  );
}

function NativeChatBubbleEllipsesIcon() {
  return (
    <svg
      className="h-[22px] w-[22px]"
      viewBox="0 0 512 512"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M432 64H80a48.05 48.05 0 0 0-48 48v240a48.05 48.05 0 0 0 48 48h96v80a16 16 0 0 0 26.49 12.09L313.6 400H432a48.05 48.05 0 0 0 48-48V112a48.05 48.05 0 0 0-48-48ZM160 256a32 32 0 1 1 32-32 32 32 0 0 1-32 32Zm96 0a32 32 0 1 1 32-32 32 32 0 0 1-32 32Zm96 0a32 32 0 1 1 32-32 32 32 0 0 1-32 32Z" />
    </svg>
  );
}

export function ChatWidget({
  poolCode,
  poolName,
  traderCode,
  displayName,
  isAuthenticated,
  composerDraft = null,
  onComposerDraftConsumed,
  onOpenPromotedOrder,
}: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [pendingDraftAttachment, setPendingDraftAttachment] = useState<ChatOrderPromotionAttachment | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isMessageListReady, setIsMessageListReady] = useState(false);
  const messagesViewportRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef(new Map<string, HTMLDivElement>());
  const inputRef = useRef<HTMLInputElement>(null);
  const restoreTargetMessageIdRef = useRef<string | null>(null);
  const currentRestoredPositionRef = useRef<ChatReadPosition | null>(null);
  const canSaveReadPositionRef = useRef(false);
  const userHasScrolledRef = useRef(false);
  const restoredForOpenRef = useRef(false);
  const shouldFollowLatestRef = useRef(true);
  const skipNextBottomScrollRef = useRef(false);
  const wasOpenRef = useRef(false);
  const restoreRevealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatScrollMetricsRef = useRef({
    contentHeight: 0,
    viewportHeight: 0,
    scrollOffset: 0,
  });

  const {
    messages,
    isConnected,
    isConnecting,
    error,
    sendMessage,
    unreadCount,
    markAsRead,
    hasLoadedHistory,
  } = useChat({
    poolCode,
    traderCode,
    displayName,
    enabled: isAuthenticated && !!poolCode,
  });

  const storageKey =
    poolCode && traderCode
      ? buildChatReadPositionStorageKey({ poolCode, userKey: traderCode })
      : null;

  const getVisibleMessageIds = useCallback(() => {
    const viewport = messagesViewportRef.current;
    if (!viewport) return [];

    const viewportRect = viewport.getBoundingClientRect();
    const visibleIds: string[] = [];
    for (const [messageId, node] of messageRefs.current) {
      const rect = node.getBoundingClientRect();
      if (rect.bottom >= viewportRect.top && rect.top <= viewportRect.bottom) {
        visibleIds.push(messageId);
      }
    }
    return visibleIds;
  }, []);

  const saveLocalReadPosition = useCallback(() => {
    if (!storageKey || !restoredForOpenRef.current || !canSaveReadPositionRef.current) return;

    const anchorMessageId =
      getLastVisibleChatMessageId(messages, getVisibleMessageIds()) ??
      restoreTargetMessageIdRef.current;
    const position = getChatReadPositionForSave({
      restoredPosition: currentRestoredPositionRef.current,
      userHasScrolled: userHasScrolledRef.current,
      isNearBottom: isChatScrolledNearBottom(chatScrollMetricsRef.current),
      anchorMessageId,
    });
    if (!position) return;

    window.localStorage.setItem(storageKey, serializeChatReadPosition(position));
  }, [getVisibleMessageIds, messages, storageKey]);

  const revealMessageListAfterRestore = useCallback((delayMs = 360) => {
    if (restoreRevealTimeoutRef.current) {
      clearTimeout(restoreRevealTimeoutRef.current);
    }
    restoreRevealTimeoutRef.current = setTimeout(() => {
      restoreRevealTimeoutRef.current = null;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsMessageListReady(true);
        });
      });
    }, delayMs);
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const viewport = messagesViewportRef.current;
    if (viewport) {
      chatScrollMetricsRef.current = {
        contentHeight: viewport.scrollHeight,
        viewportHeight: viewport.clientHeight,
        scrollOffset: viewport.scrollTop,
      };
    }

    const visibleIds = getVisibleMessageIds();
    const targetMessageId = restoreTargetMessageIdRef.current;
    if (targetMessageId && visibleIds.includes(targetMessageId)) {
      canSaveReadPositionRef.current = true;
    }
    if (userHasScrolledRef.current && visibleIds.length > 0) {
      canSaveReadPositionRef.current = true;
      shouldFollowLatestRef.current = isChatScrolledNearBottom(chatScrollMetricsRef.current);
    }
    saveLocalReadPosition();
  }, [getVisibleMessageIds, saveLocalReadPosition]);

  const handleMessagesWheel = useCallback(() => {
    userHasScrolledRef.current = true;
    canSaveReadPositionRef.current = getVisibleMessageIds().length > 0;
    restoreTargetMessageIdRef.current = null;
    shouldFollowLatestRef.current = false;
  }, [getVisibleMessageIds]);

  const closeChat = useCallback(() => {
    saveLocalReadPosition();
    setIsOpen(false);
  }, [saveLocalReadPosition]);

  const handleOpenPromotedOrder = useCallback(
    (attachment: ChatOrderPromotionAttachment) => {
      closeChat();
      onOpenPromotedOrder?.(attachment);
    },
    [closeChat, onOpenPromotedOrder],
  );

  const handleClearComposerDraft = useCallback(() => {
    setInputValue("");
    setPendingDraftAttachment(null);
  }, []);

  useEffect(() => {
    if (!composerDraft) return;

    setIsOpen(true);
    setInputValue(composerDraft.text);
    setPendingDraftAttachment(composerDraft.attachment);
    requestAnimationFrame(() => inputRef.current?.focus());
    onComposerDraftConsumed?.();
  }, [composerDraft, onComposerDraftConsumed]);

  // Mark as read when opening chat
  useEffect(() => {
    if (isOpen) {
      restoredForOpenRef.current = false;
      canSaveReadPositionRef.current = false;
      userHasScrolledRef.current = false;
      shouldFollowLatestRef.current = true;
      restoreTargetMessageIdRef.current = null;
      currentRestoredPositionRef.current = null;
      if (restoreRevealTimeoutRef.current) {
        clearTimeout(restoreRevealTimeoutRef.current);
        restoreRevealTimeoutRef.current = null;
      }
      setIsMessageListReady(false);
      chatScrollMetricsRef.current = {
        contentHeight: 0,
        viewportHeight: 0,
        scrollOffset: 0,
      };
      markAsRead();
    }
  }, [isOpen, markAsRead]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      saveLocalReadPosition();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, saveLocalReadPosition]);

  useEffect(() => {
    if (isOpen && hasLoadedHistory && messages.length === 0) {
      setIsMessageListReady(true);
    }
  }, [hasLoadedHistory, isOpen, messages.length]);

  // Restore the locally saved chat anchor once messages are available for this open.
  useEffect(() => {
    if (!isOpen || restoredForOpenRef.current || !hasLoadedHistory || messages.length === 0) return;
    restoredForOpenRef.current = true;
    canSaveReadPositionRef.current = false;
    userHasScrolledRef.current = false;

    if (!messagesViewportRef.current) {
      return;
    }

    const restorePosition: ChatReadPosition | null = storageKey
      ? parseChatReadPosition(window.localStorage.getItem(storageKey))
      : null;
    const restoreTarget = resolveChatReadPositionTarget(messages, restorePosition);
    if (restoreTarget === null) return;

    const restoreIndex = restoreTarget.index;
    const targetMessage = messages[restoreIndex];
    restoreTargetMessageIdRef.current = targetMessage.id;
    currentRestoredPositionRef.current =
      restoreTarget.mode === "latest"
        ? buildChatLatestPosition()
        : buildChatAnchorPosition(restoreTarget.messageId);
    shouldFollowLatestRef.current =
      restoreTarget.mode === "latest" || restoreIndex === messages.length - 1;
    skipNextBottomScrollRef.current = true;

    const targetNode = messageRefs.current.get(targetMessage.id);
    if (targetNode) {
      targetNode.scrollIntoView({
        behavior: "auto",
        block: "end",
      });
      requestAnimationFrame(() => {
        handleMessagesScroll();
        revealMessageListAfterRestore();
      });
    } else if (restoreTarget.mode === "latest") {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      requestAnimationFrame(() => {
        handleMessagesScroll();
        revealMessageListAfterRestore();
      });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      requestAnimationFrame(() => revealMessageListAfterRestore());
    }
  }, [handleMessagesScroll, hasLoadedHistory, isOpen, messages, revealMessageListAfterRestore, storageKey]);

  // Keep the original bottom-following behavior after the initial local restore.
  useEffect(() => {
    if (isOpen && restoredForOpenRef.current && messagesEndRef.current) {
      if (!shouldFollowLatestRef.current) {
        return;
      }
      if (skipNextBottomScrollRef.current) {
        skipNextBottomScrollRef.current = false;
        return;
      }
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (restoreRevealTimeoutRef.current) {
        clearTimeout(restoreRevealTimeoutRef.current);
      }
    };
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        closeChat();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [closeChat, isOpen]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isSending) return;

    setIsSending(true);
    try {
      await sendMessage(text, pendingDraftAttachment);
      setInputValue("");
      setPendingDraftAttachment(null);
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setIsSending(false);
    }
  }, [inputValue, isSending, pendingDraftAttachment, sendMessage]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Don't render if not authenticated or no pool
  if (!isAuthenticated || !poolCode) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => {
          if (isOpen) {
            closeChat();
          } else {
            setIsOpen(true);
          }
        }}
        className={cn(
          "fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full border border-brand-hover shadow-layer transition-all duration-200 lg:bottom-4",
          "bg-brand text-background hover:bg-brand-hover hover:scale-105",
          "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-background",
          isOpen && "scale-0 opacity-0"
        )}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        <NativeChatBubbleEllipsesIcon />
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1 text-xs font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Chat Panel */}
      <div
        className={getChatWidgetPanelClassName(isOpen)}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-subtle bg-background-raised px-4 py-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-brand" />
            <div>
              <h3 className="text-body-sm font-semibold text-foreground">
                {poolName ?? "Pool Chat"}
              </h3>
              <p className="text-label text-foreground-subtle">
                {isConnecting ? (
                  "Connecting..."
                ) : isConnected ? (
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-success" />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-danger" />
                    Disconnected
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={closeChat}
            className="rounded-full p-1 text-foreground-subtle hover:bg-background hover:text-foreground transition-colors"
            aria-label="Close chat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="relative flex-1 overflow-hidden">
          {!isMessageListReady && <ChatRestoreSkeleton />}
          <div
            ref={messagesViewportRef}
            onScroll={handleMessagesScroll}
            onWheel={handleMessagesWheel}
            onTouchStart={handleMessagesWheel}
            className={cn(
              "h-full overflow-y-auto p-4 space-y-3",
              !isMessageListReady && "opacity-0"
            )}
          >
            {error && (
              <div className="rounded-lg bg-danger/10 p-3 text-center text-body-sm text-danger">
                {error}
              </div>
            )}

            {messages.length === 0 && !error && (
              <div className="flex h-full items-center justify-center text-body-sm text-foreground-subtle">
                No messages yet. Start the conversation!
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                ref={(node) => {
                  if (node) {
                    messageRefs.current.set(msg.id, node);
                  } else {
                    messageRefs.current.delete(msg.id);
                  }
                }}
              >
                <MessageBubble
                  message={msg}
                  isOwnMessage={msg.traderCode === traderCode}
                  onOpenPromotedOrder={handleOpenPromotedOrder}
                />
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="border-t border-border-subtle bg-background-raised p-3">
          {pendingDraftAttachment && (
            <div className="mb-3">
              <div className="relative">
                <OrderPromotionCard attachment={pendingDraftAttachment} />
                <button
                  type="button"
                  onClick={handleClearComposerDraft}
                  className="absolute right-2 top-2 rounded-full border border-border-subtle bg-background/90 p-1 text-foreground-subtle transition hover:text-foreground"
                  aria-label="Discard promoted order draft"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a message..."
              disabled={!isConnected || isSending}
              className={cn(
                "flex-1 rounded-input border border-border-subtle bg-background px-3 py-2 text-body-sm text-foreground",
                "placeholder:text-foreground-subtle",
                "focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            />
            <Button
              onClick={handleSend}
              disabled={!isConnected || isSending || !inputValue.trim()}
              size="sm"
              className="h-10 w-10 p-0"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  isOwnMessage: boolean;
  onOpenPromotedOrder?: (attachment: ChatOrderPromotionAttachment) => void;
}

function MessageBubble({ message, isOwnMessage, onOpenPromotedOrder }: MessageBubbleProps) {
  const promotionAttachment = getValidOrderPromotionAttachment(message.attachment);

  return (
    <div
      className={cn(
        "flex flex-col gap-1",
        isOwnMessage ? "items-end" : "items-start"
      )}
    >
      {!isOwnMessage && (
        <span className="text-label font-medium text-foreground-subtle">
          {message.traderCode}
        </span>
      )}
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-3 py-2",
          isOwnMessage
            ? "bg-brand text-black rounded-br-md"
            : "bg-background-raised text-foreground rounded-bl-md"
        )}
      >
        {promotionAttachment && (
          <div className="mb-2 min-w-60 max-w-80">
            <OrderPromotionCard
              attachment={promotionAttachment}
              compact={isOwnMessage}
              onOpenOrder={onOpenPromotedOrder}
            />
          </div>
        )}
        <p className="text-body-sm break-words">{message.message}</p>
      </div>
      <span className="text-label text-foreground-subtle">
        {formatChatMessageTimestamp(message.timestamp)}
      </span>
    </div>
  );
}

function OrderPromotionCard({
  attachment,
  compact = false,
  onOpenOrder,
}: {
  attachment: ChatOrderPromotionAttachment;
  compact?: boolean;
  onOpenOrder?: (attachment: ChatOrderPromotionAttachment) => void;
}) {
  const sideIsBuy = attachment.side === "BUY";
  const isInteractive = Boolean(onOpenOrder);
  const content = (
    <>
      <div className="flex items-center gap-2.5">
        <TeamCodeBadge
          code={attachment.teamCode}
          size={34}
          primaryColor={attachment.teamPrimaryColor}
          secondaryColor={attachment.teamSecondaryColor}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-semibold">{attachment.teamName}</p>
          <p className={cn("text-label", compact ? "text-black/65" : "text-foreground-subtle")}>
            {attachment.teamCode}
          </p>
        </div>
        <span
          className={cn(
            "rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]",
            sideIsBuy
              ? compact
                ? "bg-success/25 text-emerald-950"
                : "bg-success/20 text-success"
              : compact
                ? "bg-danger/25 text-red-950"
                : "bg-danger/20 text-danger",
          )}
        >
          {attachment.side}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <p className={cn("text-label uppercase", compact ? "text-black/60" : "text-foreground-subtle")}>
            Qty
          </p>
          <p className="text-body-sm font-semibold">{attachment.quantity}</p>
        </div>
        <div className="text-right">
          <p className={cn("text-label uppercase", compact ? "text-black/60" : "text-foreground-subtle")}>
            Price
          </p>
          <p className="text-body-sm font-semibold">{attachment.price}</p>
        </div>
      </div>
    </>
  );
  const className = cn(
    "rounded-xl border p-3 text-left shadow-sm",
    compact
      ? "border-black/10 bg-black/10 text-black"
      : "border-border-subtle bg-background-card text-foreground",
    isInteractive && "w-full transition hover:border-brand/70 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  );

  if (isInteractive) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => onOpenOrder?.(attachment)}
        aria-label={`Open promoted ${attachment.teamCode} order`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={className}>
      {content}
    </div>
  );
}
